<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import camelcaseKeys from 'camelcase-keys';
import addDays from 'date-fns/addDays';
import differenceInCalendarDays from 'date-fns/differenceInCalendarDays';
import fromUnixTime from 'date-fns/fromUnixTime';
import getDay from 'date-fns/getDay';
import getUnixTime from 'date-fns/getUnixTime';
import startOfDay from 'date-fns/startOfDay';

import { useAlert } from 'dashboard/composables';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAccountStatusIndicator } from 'dashboard/composables/useAccountStatusIndicator';
import { resolveLocale } from 'helpers/DateHelper';
import ReportsAPI from 'dashboard/api/reports';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const props = defineProps({
  userId: {
    type: [Number, String],
    required: true,
  },
  from: {
    type: Number,
    default: 0,
  },
  to: {
    type: Number,
    default: 0,
  },
});

// Beyond this many days the columns get too narrow to read, so the widget hides itself.
const MAX_DAYS = 10;
const HOURS = Array.from({ length: 24 }, (_, hour) => hour);
const DAYS_OF_WEEK = [
  'SUNDAY',
  'MONDAY',
  'TUESDAY',
  'WEDNESDAY',
  'THURSDAY',
  'FRIDAY',
  'SATURDAY',
];
// Built-in statuses keep their semantic (theme-aware) color; custom ones are painted
// with the hex configured in Settings → Availability statuses.
const STATUS_CLASSES = {
  online: 'bg-n-teal-9 text-white',
  busy: 'bg-n-amber-9 text-white',
  // offline fills most of the day, so it stays muted and lets the active blocks stand out
  offline: 'bg-n-slate-5 text-n-slate-11',
};

const { t, te, locale } = useI18n();
const store = useStore();
const statuses = useMapGetter('accountStatuses/getStatuses');
const { indicatorFor } = useAccountStatusIndicator();

const intervals = ref([]);
const isLoading = ref(false);

const dayCount = computed(() => {
  if (!props.from || !props.to) return 0;
  return (
    differenceInCalendarDays(fromUnixTime(props.to), fromUnixTime(props.from)) +
    1
  );
});

const isVisible = computed(
  () => dayCount.value > 0 && dayCount.value <= MAX_DAYS
);

const days = computed(() => {
  const first = startOfDay(fromUnixTime(props.from));
  return Array.from({ length: dayCount.value }, (_, index) =>
    addDays(first, index)
  );
});

const statusFor = key => statuses.value.find(status => status.key === key);

// Custom statuses carry their own description; built-ins are translated. Statuses
// recorded before a key was renamed (or removed) fall back to the raw key.
const statusLabel = key => {
  const status = statusFor(key);
  if (status && !status.builtin) return status.description;
  const translationKey = `PROFILE_SETTINGS.FORM.AVAILABILITY.STATUS.${key.toUpperCase()}`;
  return te(translationKey) ? t(translationKey) : key;
};

// Custom statuses are painted with their configured hex, so they need a text color of their own.
const indicatorStyleFor = key => {
  const indicator = indicatorFor(key, STATUS_CLASSES);
  if (!indicator) return { class: 'bg-n-slate-8 text-white' };
  return indicator.style ? { ...indicator, class: 'text-white' } : indicator;
};

const timeFormatter = computed(
  () =>
    new Intl.DateTimeFormat(resolveLocale(locale.value), {
      timeStyle: 'short',
    })
);

const dateFormatter = computed(
  () =>
    new Intl.DateTimeFormat(resolveLocale(locale.value), {
      day: 'numeric',
      month: 'short',
    })
);

const hourLabel = hour =>
  new Intl.DateTimeFormat(resolveLocale(locale.value), {
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).format(new Date(2000, 0, 1, hour));

const dayName = day => t(`DAYS_OF_WEEK.${DAYS_OF_WEEK[getDay(day)]}`);

// One column per day: every interval is clipped to the day it overlaps, so an
// interval spanning midnight shows up as a block in each of the two columns.
const columns = computed(() =>
  days.value.map(day => {
    const dayStart = getUnixTime(day);
    const dayEnd = getUnixTime(addDays(day, 1));
    const dayLength = dayEnd - dayStart;
    const events = intervals.value
      .filter(
        interval => interval.endTime > dayStart && interval.startTime < dayEnd
      )
      .map(interval => {
        const start = Math.max(interval.startTime, dayStart);
        const end = Math.min(interval.endTime, dayEnd);
        const height = ((end - start) / dayLength) * 100;
        return {
          key: `${interval.status}-${start}`,
          status: interval.status,
          label: statusLabel(interval.status),
          title: `${statusLabel(interval.status)} · ${timeFormatter.value.format(fromUnixTime(start))} – ${timeFormatter.value.format(fromUnixTime(end))}`,
          indicator: indicatorStyleFor(interval.status),
          top: ((start - dayStart) / dayLength) * 100,
          height,
          // shorter blocks have no room for a caption
          showLabel: height > 3,
        };
      });
    return { day, events };
  })
);

// Every availability status configured for the account, plus any status recorded in the
// range that no longer exists (renamed or deleted since).
const legend = computed(() => {
  const keys = [
    ...new Set([
      ...statuses.value.map(status => status.key),
      ...intervals.value.map(interval => interval.status),
    ]),
  ];
  return keys.map(key => ({
    key,
    label: statusLabel(key),
    indicator: indicatorStyleFor(key),
  }));
});

const hasData = computed(() => intervals.value.length > 0);

const fetchIntervals = async () => {
  if (!isVisible.value || !props.userId) return;
  isLoading.value = true;
  try {
    const { data } = await ReportsAPI.getAgentAvailability({
      since: props.from,
      until: props.to,
      userId: props.userId,
    });
    intervals.value = camelcaseKeys(data, { deep: true });
  } catch {
    useAlert(t('REPORT.DATA_FETCHING_FAILED'));
  } finally {
    isLoading.value = false;
  }
};

onMounted(() => store.dispatch('accountStatuses/get'));

watch(() => [props.userId, props.from, props.to], fetchIntervals, {
  immediate: true,
});
</script>

<template>
  <div
    v-if="isVisible"
    class="px-6 py-5 mt-4 shadow outline-1 outline outline-n-container rounded-xl bg-n-solid-2"
  >
    <div class="flex items-center justify-between gap-4">
      <div class="flex items-baseline gap-2">
        <h5 class="text-base font-medium text-n-slate-12">
          {{ $t('AGENT_REPORTS.AVAILABILITY_CALENDAR.HEADER') }}
        </h5>
        <span class="text-sm text-n-slate-11">
          {{ $t('AGENT_REPORTS.AVAILABILITY_CALENDAR.SUBTITLE') }}
        </span>
      </div>
      <div v-if="legend.length" class="flex flex-wrap items-center gap-3">
        <span
          v-for="item in legend"
          :key="item.key"
          class="flex items-center gap-1.5 text-xs text-n-slate-11"
        >
          <span
            class="w-2.5 h-2.5 rounded-sm"
            :class="item.indicator.class"
            :style="item.indicator.style"
          />
          {{ item.label }}
        </span>
      </div>
    </div>

    <div v-if="isLoading" class="flex justify-center py-20">
      <Spinner :size="32" class="text-n-brand" />
    </div>
    <div v-else-if="!hasData" class="flex justify-center py-20">
      <span class="text-sm text-n-slate-10">
        {{ $t('AGENT_REPORTS.AVAILABILITY_CALENDAR.NO_DATA') }}
      </span>
    </div>
    <div v-else class="mt-4 overflow-x-auto">
      <div class="flex min-w-[640px] gap-px">
        <div class="flex flex-col flex-shrink-0 w-12">
          <div class="h-10" />
          <div class="flex flex-col h-[576px]">
            <div
              v-for="hour in HOURS"
              :key="hour"
              class="flex items-start justify-end flex-1 pe-2 text-[10px] text-n-slate-10"
            >
              {{ hourLabel(hour) }}
            </div>
          </div>
        </div>

        <div
          v-for="column in columns"
          :key="column.day.toISOString()"
          class="flex flex-col flex-1 min-w-[56px]"
        >
          <div class="flex flex-col items-center justify-center h-10">
            <span class="text-xs font-medium truncate text-n-slate-12">
              {{ dayName(column.day) }}
            </span>
            <time class="text-[10px] text-n-slate-10">
              {{ dateFormatter.format(column.day) }}
            </time>
          </div>
          <div
            class="relative h-[576px] border-s border-n-weak bg-n-solid-1 rounded-sm"
          >
            <div class="absolute inset-0 flex flex-col">
              <div
                v-for="hour in HOURS"
                :key="hour"
                class="flex-1 border-t border-n-weak/60"
              />
            </div>
            <div class="absolute inset-x-0.5 inset-y-0">
              <div
                v-for="event in column.events"
                :key="event.key"
                class="absolute inset-x-0 px-1 overflow-hidden text-[10px] leading-tight rounded-sm min-h-[2px]"
                :class="event.indicator.class"
                :style="{
                  top: `${event.top}%`,
                  height: `${event.height}%`,
                  ...event.indicator.style,
                }"
                :title="event.title"
              >
                <span v-if="event.showLabel" class="block truncate">
                  {{ event.label }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
